import { Link } from 'react-router-dom';
import SeoHead from '@/components/feature/SeoHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.discoveringmarche.com';

const PRIVACY_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Privacy Policy — Discovering Marche',
  description: 'Informativa sul trattamento dei dati personali ai sensi del Regolamento UE 2016/679 (GDPR) — Discovering Marche di Ines Felicity Di Nallo.',
  url: `${SITE_URL}/privacy-policy`,
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: `${SITE_URL}/privacy-policy` },
    ],
  },
};

const PrivacyPolicyPage = () => {
  return (
    <main className="min-h-screen bg-ivory-50">
      <SeoHead
        title="Privacy Policy — Discovering Marche | GDPR"
        description="Informativa sul trattamento dei dati personali ai sensi del Regolamento UE 2016/679 (GDPR) — Discovering Marche di Ines Felicity Di Nallo, agente immobiliare abilitata CCIAA Ancona."
        keywords="privacy policy, GDPR, trattamento dati personali, Discovering Marche"
        canonical="/privacy-policy"
        jsonLd={PRIVACY_JSONLD}
      />

      {/* Hero */}
      <section className="bg-anthracite-800 pt-36 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">
            Regolamento UE 2016/679 · GDPR
          </p>
          <h1 className="font-serif text-4xl md:text-5xl text-white font-light leading-tight mb-6">
            Privacy Policy
          </h1>
          <p className="font-sans text-sm text-white/50 leading-relaxed">
            Informativa sul trattamento dei dati personali ai sensi dell&apos;art. 13 del Regolamento UE 2016/679 (GDPR).
            Ultimo aggiornamento: <strong className="text-white/70">aprile 2026</strong>
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto space-y-14">

          {/* 1. Titolare */}
          <div>
            <h2 className="font-serif text-2xl text-anthracite-800 font-light mb-6 pb-3 border-b border-ivory-200">
              1. Titolare del trattamento
            </h2>
            <div className="font-sans text-sm text-anthracite-500 leading-relaxed space-y-2">
              <p><strong className="text-anthracite-700">Ines Felicity Di Nallo</strong></p>
              <p>Agente Immobiliare Abilitata — CCIAA Ancona</p>
              <p>Sede operativa: Castelfidardo (AN), Italia</p>
              <p>P.IVA: 03036380420</p>
              <p>
                Email:{' '}
                <a
                  href="mailto:ines-felicity.di-nallo@iad-italia.it"
                  className="text-terracotta-400 hover:text-terracotta-500 transition-colors"
                >
                  ines-felicity.di-nallo@iad-italia.it
                </a>
              </p>
              <p>Sito web: <a href="https://www.discoveringmarche.com" rel="nofollow" className="text-terracotta-400 hover:text-terracotta-500 transition-colors">www.discoveringmarche.com</a></p>
            </div>
          </div>

          {/* 2. Dati raccolti */}
          <div>
            <h2 className="font-serif text-2xl text-anthracite-800 font-light mb-6 pb-3 border-b border-ivory-200">
              2. Dati personali raccolti
            </h2>
            <div className="font-sans text-sm text-anthracite-500 leading-relaxed space-y-4">
              <p>Il sito raccoglie i seguenti dati personali:</p>
              <div className="space-y-3">
                {[
                  {
                    title: 'Dati forniti volontariamente',
                    desc: 'Nome, cognome, indirizzo email, numero di telefono e messaggio inseriti nei moduli di contatto presenti sul sito.',
                  },
                  {
                    title: 'Dati di navigazione',
                    desc: 'Indirizzo IP, tipo di browser, sistema operativo, pagine visitate, data e ora di accesso. Questi dati sono raccolti automaticamente dai sistemi informatici del sito.',
                  },
                  {
                    title: 'Cookie',
                    desc: 'Il sito utilizza cookie tecnici necessari al funzionamento e, previo consenso, cookie analitici e di profilazione. Per maggiori informazioni consulta la Cookie Policy.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4 items-start">
                    <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-terracotta-300"></span>
                    <div>
                      <strong className="text-anthracite-700">{item.title}:</strong>{' '}
                      {item.desc}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 3. Finalità */}
          <div>
            <h2 className="font-serif text-2xl text-anthracite-800 font-light mb-6 pb-3 border-b border-ivory-200">
              3. Finalità e base giuridica del trattamento
            </h2>
            <div className="font-sans text-sm text-anthracite-500 leading-relaxed">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-ivory-200">
                      <th className="py-3 pr-6 font-sans text-xs tracking-widest uppercase text-anthracite-400 font-normal">Finalità</th>
                      <th className="py-3 pr-6 font-sans text-xs tracking-widest uppercase text-anthracite-400 font-normal">Base giuridica</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ivory-200">
                    {[
                      { finalita: 'Rispondere alle richieste di contatto e consulenza', base: 'Esecuzione di misure precontrattuali (art. 6.1.b GDPR)' },
                      { finalita: 'Invio di comunicazioni commerciali e newsletter (previo consenso)', base: 'Consenso dell\'interessato (art. 6.1.a GDPR)' },
                      { finalita: 'Adempimento di obblighi legali e fiscali', base: 'Obbligo legale (art. 6.1.c GDPR)' },
                      { finalita: 'Analisi statistica del traffico del sito (anonimizzata)', base: 'Legittimo interesse (art. 6.1.f GDPR)' },
                      { finalita: 'Funzionamento tecnico del sito', base: 'Legittimo interesse (art. 6.1.f GDPR)' },
                    ].map((row, i) => (
                      <tr key={i}>
                        <td className="py-3 pr-6 text-anthracite-600">{row.finalita}</td>
                        <td className="py-3 text-anthracite-400">{row.base}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* 4. Conservazione */}
          <div>
            <h2 className="font-serif text-2xl text-anthracite-800 font-light mb-6 pb-3 border-b border-ivory-200">
              4. Periodo di conservazione
            </h2>
            <div className="font-sans text-sm text-anthracite-500 leading-relaxed space-y-3">
              <p>I dati personali sono conservati per il tempo strettamente necessario alle finalità per cui sono stati raccolti:</p>
              <div className="space-y-2">
                {[
                  'Dati di contatto: fino a 24 mesi dall\'ultima interazione, salvo obblighi di legge.',
                  'Dati per newsletter: fino alla revoca del consenso.',
                  'Dati di navigazione: massimo 12 mesi.',
                  'Dati fiscali e contabili: 10 anni ai sensi della normativa fiscale italiana.',
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 items-start">
                    <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-terracotta-300"></span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 5. Condivisione */}
          <div>
            <h2 className="font-serif text-2xl text-anthracite-800 font-light mb-6 pb-3 border-b border-ivory-200">
              5. Condivisione dei dati con terze parti
            </h2>
            <div className="font-sans text-sm text-anthracite-500 leading-relaxed space-y-3">
              <p>I dati personali non vengono venduti né ceduti a terzi per finalità commerciali. Possono essere comunicati a:</p>
              <div className="space-y-2">
                {[
                  'Fornitori di servizi tecnici (hosting, email, CRM) che agiscono come responsabili del trattamento ai sensi dell\'art. 28 GDPR.',
                  'Professionisti (notai, avvocati, commercialisti) coinvolti nelle pratiche immobiliari, nei limiti strettamente necessari.',
                  'Autorità pubbliche, nei casi previsti dalla legge.',
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 items-start">
                    <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-terracotta-300"></span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4">
                I dati non vengono trasferiti al di fuori dello Spazio Economico Europeo (SEE), salvo che con le garanzie previste dal GDPR (decisioni di adeguatezza, clausole contrattuali standard).
              </p>
            </div>
          </div>

          {/* 6. Diritti */}
          <div>
            <h2 className="font-serif text-2xl text-anthracite-800 font-light mb-6 pb-3 border-b border-ivory-200">
              6. Diritti dell&apos;interessato
            </h2>
            <div className="font-sans text-sm text-anthracite-500 leading-relaxed space-y-3">
              <p>Ai sensi degli artt. 15–22 del GDPR, hai il diritto di:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {[
                  { icon: 'ri-eye-line', title: 'Accesso', desc: 'Ottenere conferma del trattamento e copia dei tuoi dati.' },
                  { icon: 'ri-edit-line', title: 'Rettifica', desc: 'Correggere dati inesatti o incompleti.' },
                  { icon: 'ri-delete-bin-line', title: 'Cancellazione', desc: 'Richiedere la cancellazione dei tuoi dati ("diritto all\'oblio").' },
                  { icon: 'ri-pause-circle-line', title: 'Limitazione', desc: 'Limitare il trattamento in determinati casi.' },
                  { icon: 'ri-download-line', title: 'Portabilità', desc: 'Ricevere i tuoi dati in formato strutturato e leggibile.' },
                  { icon: 'ri-close-circle-line', title: 'Opposizione', desc: 'Opporti al trattamento basato su legittimo interesse.' },
                  { icon: 'ri-shield-check-line', title: 'Revoca consenso', desc: 'Revocare il consenso in qualsiasi momento, senza pregiudizio.' },
                  { icon: 'ri-customer-service-line', title: 'Reclamo', desc: 'Presentare reclamo al Garante Privacy (www.garanteprivacy.it).' },
                ].map((item) => (
                  <div key={item.title} className="flex gap-3 items-start p-4 bg-white rounded-lg border border-ivory-200">
                    <div className="w-8 h-8 flex items-center justify-center shrink-0 rounded-full bg-terracotta-50 border border-terracotta-100">
                      <i className={`${item.icon} text-terracotta-400 text-sm`}></i>
                    </div>
                    <div>
                      <strong className="text-anthracite-700 text-xs uppercase tracking-wide">{item.title}</strong>
                      <p className="mt-0.5 text-anthracite-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4">
                Per esercitare i tuoi diritti, scrivi a:{' '}
                <a
                  href="mailto:ines-felicity.di-nallo@iad-italia.it"
                  className="text-terracotta-400 hover:text-terracotta-500 transition-colors"
                >
                  ines-felicity.di-nallo@iad-italia.it
                </a>
              </p>
            </div>
          </div>

          {/* 7. Cookie */}
          <div>
            <h2 className="font-serif text-2xl text-anthracite-800 font-light mb-6 pb-3 border-b border-ivory-200">
              7. Cookie
            </h2>
            <div className="font-sans text-sm text-anthracite-500 leading-relaxed space-y-3">
              <p>
                Il sito utilizza cookie tecnici necessari al funzionamento e, previo consenso esplicito, cookie analitici per comprendere come gli utenti interagiscono con il sito.
              </p>
              <p>
                Puoi gestire le tue preferenze sui cookie in qualsiasi momento tramite il pannello &quot;Gestisci Cookie&quot; presente nel footer del sito, oppure modificando le impostazioni del tuo browser.
              </p>
              <p>
                Per informazioni dettagliate sui cookie utilizzati, consulta la nostra Cookie Policy accessibile dal footer.
              </p>
            </div>
          </div>

          {/* 8. Sicurezza */}
          <div>
            <h2 className="font-serif text-2xl text-anthracite-800 font-light mb-6 pb-3 border-b border-ivory-200">
              8. Sicurezza dei dati
            </h2>
            <div className="font-sans text-sm text-anthracite-500 leading-relaxed">
              <p>
                Adottiamo misure tecniche e organizzative adeguate per proteggere i dati personali da accessi non autorizzati, perdita, distruzione o divulgazione. Il sito utilizza il protocollo HTTPS per la trasmissione sicura dei dati. L&apos;accesso ai dati è limitato alle sole persone autorizzate e strettamente necessarie.
              </p>
            </div>
          </div>

          {/* 9. Modifiche */}
          <div>
            <h2 className="font-serif text-2xl text-anthracite-800 font-light mb-6 pb-3 border-b border-ivory-200">
              9. Modifiche alla presente informativa
            </h2>
            <div className="font-sans text-sm text-anthracite-500 leading-relaxed">
              <p>
                Il Titolare si riserva il diritto di modificare la presente informativa in qualsiasi momento, in particolare in caso di variazioni normative o tecnologiche. Le modifiche saranno pubblicate su questa pagina con indicazione della data di aggiornamento. Si consiglia di consultare periodicamente questa pagina.
              </p>
            </div>
          </div>

          {/* 10. Contatti */}
          <div className="bg-white rounded-lg border border-ivory-200 p-8">
            <h2 className="font-serif text-2xl text-anthracite-800 font-light mb-4">
              Hai domande sulla tua privacy?
            </h2>
            <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-6">
              Per qualsiasi richiesta relativa al trattamento dei tuoi dati personali, contattaci direttamente.
            </p>
            <Link
              to="/contatti"
              className="inline-block font-sans text-xs tracking-widest uppercase px-8 py-3 bg-terracotta-300 text-white hover:bg-terracotta-400 transition-colors rounded-full cursor-pointer whitespace-nowrap"
            >
              Contattaci
            </Link>
          </div>

        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicyPage;
